import styled from '@emotion/styled';
import { Divider as _Divider, Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectType } from '../../../generated/graphql';
import { itemBackground } from '../../constant/color';
import { subText } from '../../constant/color';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';
import AspectRatioFixedImage from '../primitive/AspectRatioFixedImage';
import LfToBr from '../primitive/LfToBr';

export type Variant = 'card';

interface Props {
  projectType: Pick<
    ProjectType,
    'title' | 'thumbnailImageUrl' | 'expectedDeliveryPeriod' | 'outputSpecification' | 'regulation'
  >;
  variant?: Variant;
  className?: string;
}

const ProjectTypeDetail = ({ projectType, variant, className }: ClassAttributes<HTMLElement> & Props) => (
  <Root className={className} data-variant={variant}>
    <Title variant="h6">{projectType.title}</Title>

    <Thumbnail>
      <ThumbnailImage>
        <AspectRatioFixedImage src={projectType.thumbnailImageUrl} aspectRatio={[16, 9]} />
      </ThumbnailImage>
    </Thumbnail>

    <Body>
      <Heading variant="subtitle1">
        <FormattedMessage id="project_type_detail.expected_time_to_delivery" />
      </Heading>

      <Description>
        <LfToBr text={projectType.expectedDeliveryPeriod} />
      </Description>

      <Divider />

      <Heading variant="subtitle1">
        <FormattedMessage id="project_type_detail.specification" />
      </Heading>

      <Description>
        <LfToBr text={projectType.outputSpecification} />
      </Description>

      <Divider />

      <Heading variant="subtitle1">
        <FormattedMessage id="project_type_detail.things_you_need" />
      </Heading>

      <Description>
        <LfToBr text={projectType.regulation} />
      </Description>
    </Body>
  </Root>
);

const Root = styled('div')((props: any) => {
  if ((props['data-variant'] as Variant) !== 'card') {
    return {
      ...gridLayoutContainer({
        columns: ['276px', '24px', 'auto'],
        rows: ['auto', 'auto'],
        areas: [
          ['title', 'title', 'title'],
          ['thumbnail', '.', 'body'],
        ],
      }),
      [mq('sp')]: {
        ...gridLayoutContainer({
          columns: ['1fr'],
          rows: ['auto', 'auto', 'auto'],
          areas: [['title'], ['thumbnail'], ['body']],
        }),
      },
    };
  }
});

const Title = styled(Typography)(gridLayoutItem('title'), {
  marginBottom: SPACING_LARGE,
  wordBreak: 'break-word',
});

const Thumbnail = styled('div')(gridLayoutItem('thumbnail'), {
  marginBottom: SPACING_UNIT * 3,
});

const ThumbnailImage = styled('div')({
  backgroundColor: itemBackground,
});

const Body = styled('div')(gridLayoutItem('body'));

const Heading = styled(Typography)({
  marginTop: SPACING_UNIT * 3,
  marginBottom: SPACING_REGULAR,
  '&:first-of-type': {
    marginTop: 0,
  },
  fontWeight: 700,
  [mq('sp')]: {
    fontSize: 12,
    fontWeight: 400,
    marginBottom: 4,
    color: subText,
  },
});

const Description = styled(Typography)({
  marginBottom: SPACING_UNIT * 3,
  lineHeight: 1.4,
  wordBreak: 'break-word',
});

const Divider = styled(_Divider)({
  [mq('sp')]: {
    display: 'none',
  },
});

export default ProjectTypeDetail;
